import React, { useState, useEffect } from 'react';
import api from './api';

// ─── Comparatif par appellation, à conditionnement égal ──────────────────────
// Comparer deux prix n'a de sens que si l'on compare la même chose : le
// regroupement se fait sur (volume unitaire, nombre d'unités), jamais sur le
// seul équivalent-bouteille — 6 désigne aussi bien un carton de 6 bouteilles
// qu'un carton de 3 magnums.

const euro = (n, d) => (n == null ? '—' : Number(n).toLocaleString('fr-FR', { style: 'currency', currency: d || 'EUR' }));

const libelleCond = (c) => {
  const vol = Number(c.volume_unitaire_l);
  const format = vol === 0.75 ? 'bouteille' : vol === 1.5 ? 'magnum' : `${vol} L`;
  return c.nb_unites > 1 ? `${c.nb_unites} × ${format}` : format.charAt(0).toUpperCase() + format.slice(1);
};

export default function Comparatif() {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [seulsComparables, setSeulsComparables] = useState(true);

  useEffect(() => {
    api.get('/veille/comparatif')
      .then((r) => setData(r.data))
      .catch(() => setErreur('Comparatif indisponible.'));
  }, []);

  if (erreur) return <p className="alerte">{erreur}</p>;
  if (!data) return <p className="attente">Chargement…</p>;

  const appellations = data.appellations
    .map((a) => ({ ...a, conditionnements: a.conditionnements.filter((c) => !seulsComparables || c.comparable) }))
    .filter((a) => a.conditionnements.length > 0);

  const nbComparables = data.appellations.reduce((s, a) => s + a.conditionnements.filter((c) => c.comparable).length, 0);

  return (
    <>
      <div className="barre">
        <label className="bascule">
          <input type="checkbox" checked={seulsComparables} onChange={(e) => setSeulsComparables(e.target.checked)} />
          Uniquement ce qui est présent sur plusieurs sites
        </label>
        <span className="discret">{nbComparables} comparaison{nbComparables > 1 ? 's' : ''} possible{nbComparables > 1 ? 's' : ''} · {data.devise}</span>
      </div>

      <p className="note">
        Les prix ne sont rapprochés qu'à <strong>conditionnement identique</strong> — même
        contenance et même nombre d'unités. Au sein d'une appellation, chaque site est
        représenté par son offre la moins chère : c'est un prix de marché par appellation,
        pas une comparaison cuvée par cuvée.
        {data.devise === 'EUR' && ' Les offres en francs suisses sont exclues : les convertir sans taux donnerait un classement faux.'}
      </p>

      {appellations.length === 0 && <p className="discret centre">Aucune appellation n'est présente sur plusieurs sites.</p>}

      {appellations.map((a) => (
        <section key={a.appellation} className="appellation">
          <h2>{a.appellation}</h2>
          {a.conditionnements.map((c) => (
            <div key={`${c.volume_unitaire_l}-${c.nb_unites}`} className="cond">
              <div className="cond-entete">
                <span className="cond-nom">{libelleCond(c)}</span>
                {c.comparable ? (
                  <span className={c.ecart_pct >= 20 ? 'ecart-fort' : 'ecart'}>
                    écart {euro(c.ecart, data.devise)} ({c.ecart_pct} %) · au mieux chez <strong>{c.meilleur.split('—').pop().trim()}</strong>
                  </span>
                ) : (
                  <span className="discret">un seul site — pas de comparaison</span>
                )}
              </div>
              <table>
                <tbody>
                  {c.sources.sort((x, y) => x.prix_min - y.prix_min).map((s, i) => (
                    <tr key={s.site} className={i === 0 && c.comparable ? 'meilleur' : undefined}>
                      <td className="site">{s.source.split('—').pop().trim()}</td>
                      <td className="discret">{s.nb} réf.</td>
                      <td>
                        <a href={s.url} target="_blank" rel="noopener noreferrer">{s.moins_cher}</a>
                      </td>
                      <td className="droite nombre">{euro(s.prix_min, data.devise)}</td>
                      <td className="droite discret" title="Prix ramené à la bouteille de 75 cl">
                        {c.nb_unites > 1 || Number(c.volume_unitaire_l) !== 0.75 ? `${euro(s.unitaire_min, data.devise)} / btl` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </section>
      ))}
    </>
  );
}
